import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { DocumentNode } from "graphql";
import gql from "graphql-tag";
import { AlertTriangle, WandSparklesIcon } from "lucide-react";
import { forwardRef, useImperativeHandle, useState } from "react";
import { FieldPath, SubmitHandler, useFieldArray, useForm } from "react-hook-form";
import { toast } from "sonner";
import { useMutation } from "urql";
import { z } from "zod";

import { ControlledCheckboxInput } from "@/components/FormInputs";
import { SearchAndSelectPlayersInput, SearchSinglePlayerInput } from "@/components/SearchInput";
import { DateTimeInput } from "@/components/inputs/DateTimeInput";
import EditorInput from "@/components/inputs/EditorInput";
import { ImageUploadInput } from "@/components/inputs/ImageInput";
import { LayoutTemplateSelector } from "@/components/inputs/LayoutTemplateSelector";
import { PlayerIdInput } from "@/components/inputs/PlayerIdInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
    Form,
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { extractChessresultsTournamentId } from "@/utils/chessresults";
import { toPlayerFragment } from "@/utils/player";

import {
    TournamentInput,
    TournamentStandingsCalculationInput,
    zFetchChessresultsPlayersResponse,
    zTournamentInput,
} from "./validation/tournament";

// Helper functions for Chess.com slug generation
const generateSlugPrefix = (date: Date): string => {
    const monthNames = [
        "january",
        "february",
        "march",
        "april",
        "may",
        "june",
        "july",
        "august",
        "september",
        "october",
        "november",
        "december",
    ];
    const month = monthNames[date.getMonth()];
    const day = String(date.getDate()).padStart(2, "0");
    const year = date.getFullYear();
    return `titled-tuesday-blitz-${month}-${day}-${year}-`;
};

const extractSevenDigitId = (slug: string): string | null => {
    const match = slug.match(/\d{7}/);
    return match ? match[0] : null;
};

const validateSlugFormat = (slug: string | null | undefined, date: Date): boolean => {
    if (!slug) return true; // Empty is valid (optional field)

    const expectedPrefix = generateSlugPrefix(date);
    const pattern = new RegExp(`^${expectedPrefix}\\d{7}$`);
    return pattern.test(slug);
};

const canGenerateSlug = (currentValue: string | null | undefined): boolean => {
    if (!currentValue || currentValue === "") return true;

    // Check if it's empty or only contains a 7-digit number
    return /^\d{7}$/.test(currentValue);
};

export const convertGraphQLToForm = (
    standingsCalculation: unknown
): TournamentStandingsCalculationInput | null => {
    if (!standingsCalculation || typeof standingsCalculation !== "object") {
        return { type: "inherit" } as TournamentStandingsCalculationInput;
    }

    const sc = standingsCalculation as Record<string, unknown>;
    const method =
        typeof sc.method === "object" && sc.method != null
            ? (sc.method as Record<string, unknown>)
            : null;
    if (!method || typeof method.t !== "string") {
        return { type: "inherit" } as TournamentStandingsCalculationInput;
    }

    const playerRankCap = typeof sc.player_rank_cap === "number" ? sc.player_rank_cap : null;

    switch (method.t) {
        case "Points":
            return { type: "points", playerRankCap } as TournamentStandingsCalculationInput;
        case "Manual":
            return { type: "manual", playerRankCap } as TournamentStandingsCalculationInput;
        case "Rank": {
            const content =
                typeof method.c === "object" && method.c != null
                    ? (method.c as Record<string, unknown>)
                    : {};
            const minPoints = typeof content.min_points === "number" ? content.min_points : 0;
            const knockout = typeof content.knockout === "boolean" ? content.knockout : true;
            const rankPointsObj =
                typeof content.rank_points === "object" && content.rank_points != null
                    ? (content.rank_points as Record<string, unknown>)
                    : {};

            const ordered = Object.keys(rankPointsObj)
                .map(k => Number(k))
                .filter(n => !Number.isNaN(n))
                .sort((a, b) => a - b);

            const arr: number[] = [];
            for (const rank of ordered) {
                const v = rankPointsObj[String(rank)];
                if (typeof v === "number") arr[rank - 1] = v;
            }

            return {
                type: "rank",
                playerRankCap,
                minPoints,
                rankPointsString: arr.join(","),
                knockout,
            } as TournamentStandingsCalculationInput;
        }
        default:
            return { type: "inherit" } as TournamentStandingsCalculationInput;
    }
};

export const convertFormToGraphQL = (data?: TournamentStandingsCalculationInput | null) => {
    if (!data) {
        return null;
    }

    if (data.type === "points") {
        return {
            player_rank_cap: data.playerRankCap,
            method: {
                t: "Points",
            },
        };
    } else if (data.type === "manual") {
        return {
            player_rank_cap: data.playerRankCap,
            method: {
                t: "Manual",
            },
        };
    } else if (data.type === "rank") {
        // Parse comma-separated string into rank_points object
        const rankPointsMap: Record<number, number> = {};
        if (data.rankPointsString) {
            const points = data.rankPointsString.split(",").map(p => p.trim());
            points.forEach((point, index) => {
                const pointValue = parseInt(point);
                if (!isNaN(pointValue)) {
                    rankPointsMap[index + 1] = pointValue;
                }
            });
        }

        return {
            player_rank_cap: data.playerRankCap,
            method: {
                t: "Rank",
                c: {
                    min_points: data.minPoints,
                    rank_points: rankPointsMap,
                    knockout: data.knockout ?? true,
                },
            },
        };
    } else if (data.type === "inherit") {
        return null;
    }

    return null;
};

// Define the interface for methods exposed via ref
// Example usage:
// const formRef = useRef<TournamentFormRef>(null);
// formRef.current?.setError("title", { message: "Title is required" });
// formRef.current?.reset();
export interface TournamentFormRef {
    setError: (field: FieldPath<TournamentInput>, error: { message: string }) => void;
    clearErrors: () => void;
    reset: () => void;
    getValues: () => TournamentInput;
}

interface TournamentFormProps {
    onSubmit: SubmitHandler<TournamentInput>;
    defaultValues: TournamentInput;
    isCreateMode?: boolean;
}

const fetchChessresultsPlayersMutation: DocumentNode = gql`
    mutation fetchChessresultsPlayers($input: FetchChessresultsPlayersInput!) {
        fetchChessresultsPlayers(input: $input) {
            id
            name
            fideId
            nickname
            imageId
        }
    }
`;

export const TournamentForm = forwardRef<TournamentFormRef, TournamentFormProps>(
    ({ onSubmit, defaultValues, isCreateMode = false }, ref) => {
        const form = useForm<z.infer<typeof zTournamentInput>>({
            defaultValues: defaultValues ?? zTournamentInput.parse({}),
            mode: "onChange",
            reValidateMode: "onChange",
            resolver: zodResolver(zTournamentInput),
        });

        const { fields, append, remove } = useFieldArray({
            control: form.control,
            name: "manualStandingsList",
        });

        const [chessresultsTournamentId, setChessresultsTournamentId] = useState("");
        const [_, fetchChessresultsPlayers] = useMutation(fetchChessresultsPlayersMutation);

        // Expose form methods via ref
        useImperativeHandle(
            ref,
            () => ({
                setError: (field, error) => form.setError(field, error),
                clearErrors: () => form.clearErrors(),
                reset: () => form.reset(),
                getValues: () => form.getValues(),
            }),
            [form]
        );

        const handleFetchPlayers = async () => {
            const extractedId = extractChessresultsTournamentId(chessresultsTournamentId);

            if (!extractedId) {
                toast.error("Enter a chessresults tournament ID or URL");
                return;
            }

            const result = await fetchChessresultsPlayers({
                input: { chessresultsTournamentId: extractedId },
            });

            if (result.error) {
                toast.error("Error fetching players from chessresults", {
                    description: result.error.message,
                });
                return;
            }

            const validation = zFetchChessresultsPlayersResponse.safeParse(result.data);

            if (!validation.success) {
                toast.error("Something went wrong: Invalid API response");
                return;
            }

            form.setValue("players", validation.data.fetchChessresultsPlayers);
            toast.success("Players fetched successfully");
        };

        return (
            <Form {...form}>
                <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
                    <FormField
                        control={form.control}
                        name="title"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Title</FormLabel>
                                <FormControl>
                                    <Input placeholder="Title" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="shortTitle"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Short title</FormLabel>
                                <FormControl>
                                    <Input placeholder="Short title" {...field} />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="startAt"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Start date</FormLabel>
                                <FormControl>
                                    <DateTimeInput
                                        value={field.value}
                                        onValue={startAt => form.setValue("startAt", startAt)}
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="endAt"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>End date</FormLabel>
                                <FormControl>
                                    <DateTimeInput
                                        value={field.value}
                                        onValue={endAt => form.setValue("endAt", endAt)}
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="description"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Description</FormLabel>
                                <FormControl>
                                    <EditorInput
                                        initialValue={field.value}
                                        onChange={value => form.setValue("description", value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="previousWinnerPlayerId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Last year's winner</FormLabel>
                                <FormControl>
                                    <PlayerIdInput
                                        className="max-w-[500px] flex-1"
                                        playerId={field.value}
                                        onPlayerIdChange={field.onChange}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="location"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Location</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Location"
                                        {...field}
                                        value={field.value ?? ""}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="format"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Format</FormLabel>
                                <FormControl>
                                    <EditorInput
                                        initialValue={field.value}
                                        onChange={value => form.setValue("format", value)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="tournamentSeriesId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Tournament Series ID (Optional)</FormLabel>
                                <FormControl>
                                    <Input
                                        placeholder="Enter tournament series ID"
                                        {...field}
                                        value={field.value ?? ""}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="chesscomInternalSlug"
                        render={({ field }) => {
                            const startDate = form.watch("startAt");
                            const currentValue = field.value;
                            const isValidFormat = validateSlugFormat(currentValue, startDate);
                            const canGenerate = canGenerateSlug(currentValue);

                            const handleGenerateSlug = () => {
                                if (!canGenerate) return;

                                const prefix = generateSlugPrefix(startDate);
                                const existingId = extractSevenDigitId(currentValue || "");
                                const newSlug = existingId ? `${prefix}${existingId}` : prefix;
                                form.setValue("chesscomInternalSlug", newSlug);
                            };

                            return (
                                <FormItem>
                                    <FormLabel>Chess.com Internal Slug (Optional)</FormLabel>
                                    <FormControl>
                                        <div className="flex items-center gap-2">
                                            <Input
                                                placeholder="titled-tuesday-blitz-september-02-2025-5905663"
                                                {...field}
                                                value={field.value ?? ""}
                                                className="flex-1"
                                            />
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="icon"
                                                            onClick={handleGenerateSlug}
                                                            disabled={!canGenerate}
                                                        >
                                                            <WandSparklesIcon className="h-4 w-4" />
                                                        </Button>
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>
                                                            Generate slug from tournament date
                                                            and/or Chess.com internal id
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                    </FormControl>
                                    {currentValue && !isValidFormat && (
                                        <div className="mt-2 flex items-center gap-2 text-sm text-yellow-600">
                                            <AlertTriangle className="h-4 w-4" />
                                            <span>
                                                Warning: Slug doesn't match expected format
                                                (titled-tuesday-blitz-
                                                {format(startDate, "MMMM-dd-yyyy").toLowerCase()}
                                                -[7-digit-id])
                                            </span>
                                        </div>
                                    )}
                                    <FormMessage />
                                </FormItem>
                            );
                        }}
                    />

                    {isCreateMode && (
                        <Card>
                            <CardHeader>
                                <CardTitle>Initial phase layouts</CardTitle>
                                <CardDescription>
                                    Select which layout to start with for each phase of the
                                    tournament.
                                </CardDescription>
                            </CardHeader>
                            <CardContent className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="beforeLayoutTemplateId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Before tournament starts</FormLabel>
                                            <FormControl>
                                                <LayoutTemplateSelector
                                                    phase="BEFORE"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    className="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="duringLayoutTemplateId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>During tournament</FormLabel>
                                            <FormControl>
                                                <LayoutTemplateSelector
                                                    phase="DURING"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    className="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="afterLayoutTemplateId"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>After tournament ends</FormLabel>
                                            <FormControl>
                                                <LayoutTemplateSelector
                                                    phase="AFTER"
                                                    value={field.value}
                                                    onChange={field.onChange}
                                                    className="w-full"
                                                />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />
                            </CardContent>
                        </Card>
                    )}

                    <div className="space-y-2">
                        <FormLabel>Fetch players from Chessresults</FormLabel>
                        <div className="flex space-x-2">
                            <Input
                                placeholder="Chessresults tournament ID or URL"
                                value={chessresultsTournamentId}
                                onChange={e => setChessresultsTournamentId(e.target.value)}
                            />
                            <Button type="button" onClick={handleFetchPlayers}>
                                Fetch players
                            </Button>
                        </div>
                    </div>

                    <FormField
                        control={form.control}
                        name="players"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Players</FormLabel>
                                <FormControl>
                                    <SearchAndSelectPlayersInput
                                        className="w-full"
                                        selectedPlayers={field.value}
                                        onSelectedPlayersChange={players =>
                                            form.setValue("players", players ?? [])
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="heroImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Upload hero image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        className="w-full"
                                        imageId={field.value}
                                        onImageId={imageId => form.setValue("heroImageId", imageId)}
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="thumbnailImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Upload thumbnail image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        className="w-full"
                                        imageId={field.value}
                                        onImageId={imageId =>
                                            form.setValue("thumbnailImageId", imageId)
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="badgeImageId"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Upload badge image</FormLabel>
                                <FormControl>
                                    <ImageUploadInput
                                        className="w-full"
                                        imageId={field.value}
                                        onImageId={imageId =>
                                            form.setValue("badgeImageId", imageId)
                                        }
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="isInternalOnly"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Is internal only</FormLabel>
                                <FormControl>
                                    <ControlledCheckboxInput
                                        checked={field.value}
                                        onValue={isInternalOnly =>
                                            form.setValue("isInternalOnly", isInternalOnly)
                                        }
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="isHidden"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Is hidden</FormLabel>
                                <FormControl>
                                    <ControlledCheckboxInput
                                        checked={field.value}
                                        onValue={isHidden => form.setValue("isHidden", isHidden)}
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name="countryStandingsEnabled"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Enable country standings</FormLabel>
                                <FormControl>
                                    <ControlledCheckboxInput
                                        checked={field.value}
                                        onValue={value =>
                                            form.setValue("countryStandingsEnabled", value)
                                        }
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                    {form.watch("countryStandingsEnabled") && (
                        <FormField
                            control={form.control}
                            name="countryStandingsTopPlayers"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>
                                        Top players per country (leave empty for all players)
                                    </FormLabel>
                                    <FormControl>
                                        <Input
                                            type="number"
                                            {...field}
                                            value={field.value ?? ""}
                                            onChange={e => {
                                                const value = e.target.value
                                                    ? parseInt(e.target.value)
                                                    : null;
                                                form.setValue("countryStandingsTopPlayers", value);
                                            }}
                                        />
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />
                    )}

                    <FormField
                        control={form.control}
                        name="manualStandings"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Enable manual standings</FormLabel>
                                <FormControl>
                                    <ControlledCheckboxInput
                                        checked={field.value}
                                        onValue={value => form.setValue("manualStandings", value)}
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name="evaluationSurgeProtection"
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>Enable evaluation surge protection</FormLabel>
                                <FormControl>
                                    <ControlledCheckboxInput
                                        checked={field.value}
                                        onValue={value =>
                                            form.setValue("evaluationSurgeProtection", value)
                                        }
                                        className="w-full"
                                    />
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />

                    {form.watch("manualStandings") && (
                        <div className="space-y-4 rounded border p-4">
                            <h3 className="text-lg font-semibold">Manual standings</h3>
                            <div className="grid grid-cols-4 gap-2 text-sm font-medium text-gray-700">
                                <div>Player</div>
                                <div>Points</div>
                                <div>Rank Points</div>
                                <div>Action</div>
                            </div>
                            {fields.map((field, index) => (
                                <div
                                    key={field.id}
                                    className="grid grid-cols-4 items-center gap-2 rounded border p-2"
                                >
                                    <FormField
                                        control={form.control}
                                        name={`manualStandingsList.${index}.player`}
                                        render={({ field: playerField }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <SearchSinglePlayerInput
                                                        player={toPlayerFragment(playerField.value)}
                                                        onPlayerChange={player =>
                                                            playerField.onChange(
                                                                toPlayerFragment(player)
                                                            )
                                                        }
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name={`manualStandingsList.${index}.points`}
                                        render={({ field: pointsField }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        placeholder="Points"
                                                        {...pointsField}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name={`manualStandingsList.${index}.rankPoints`}
                                        render={({ field: rankPointsField }) => (
                                            <FormItem>
                                                <FormControl>
                                                    <Input
                                                        type="number"
                                                        placeholder="Rank Points"
                                                        {...rankPointsField}
                                                    />
                                                </FormControl>
                                            </FormItem>
                                        )}
                                    />

                                    <Button type="button" onClick={() => remove(index)}>
                                        Remove
                                    </Button>
                                </div>
                            ))}

                            <Button
                                type="button"
                                onClick={() =>
                                    append({ player: null, points: 0, rankPoints: undefined })
                                }
                            >
                                Add player standing
                            </Button>
                        </div>
                    )}

                    {/* Standings calculation section - only show if tournament has a series */}
                    {form.watch("tournamentSeriesId") &&
                        form.watch("tournamentSeriesId") !== "" && (
                            <Card>
                                <CardHeader>
                                    <CardTitle>Overall Standings Override</CardTitle>
                                    <CardDescription>
                                        Optional: Override how this tournament counts toward the
                                        series’ overall standings. Leave empty to inherit the
                                        tournament series configuration.
                                    </CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <FormField
                                        control={form.control}
                                        name="standingsCalculation.type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Calculation Method Override</FormLabel>
                                                <Select
                                                    onValueChange={val => field.onChange(val)}
                                                    value={field.value}
                                                >
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue placeholder="Leave empty to inherit series settings" />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        <SelectItem value="inherit">
                                                            None — inherit series settings
                                                        </SelectItem>
                                                        <SelectItem value="points">
                                                            Points
                                                        </SelectItem>
                                                        <SelectItem value="rank">Rank</SelectItem>
                                                        <SelectItem value="manual">
                                                            Manual
                                                        </SelectItem>
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    {(form.watch("standingsCalculation.type") === "points" ||
                                        form.watch("standingsCalculation.type") === "manual") && (
                                        <FormField
                                            control={form.control}
                                            name="standingsCalculation.playerRankCap"
                                            render={({ field }) => (
                                                <FormItem>
                                                    <FormLabel>Player Rank Cap</FormLabel>
                                                    <FormControl>
                                                        <Input
                                                            type="number"
                                                            placeholder="All ranks"
                                                            value={field.value ?? ""}
                                                            onChange={e => {
                                                                const val = e.target.value;
                                                                field.onChange(
                                                                    val === "" ? null : val
                                                                );
                                                            }}
                                                        />
                                                    </FormControl>
                                                    <FormMessage />
                                                </FormItem>
                                            )}
                                        />
                                    )}

                                    {form.watch("standingsCalculation.type") === "rank" && (
                                        <div className="space-y-4">
                                            <FormField
                                                control={form.control}
                                                name="standingsCalculation.minPoints"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Minimum Points</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                type="number"
                                                                placeholder="0"
                                                                {...field}
                                                                value={field.value ?? 0}
                                                                onChange={e =>
                                                                    field.onChange(
                                                                        parseInt(e.target.value) ||
                                                                            0
                                                                    )
                                                                }
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="standingsCalculation.rankPointsString"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Rank Points</FormLabel>
                                                        <FormControl>
                                                            <Input
                                                                placeholder="25,20,18,15,12,10,8,6,4,2"
                                                                {...field}
                                                            />
                                                        </FormControl>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />

                                            <FormField
                                                control={form.control}
                                                name="standingsCalculation.knockout"
                                                render={({ field }) => (
                                                    <FormItem>
                                                        <FormLabel>Knockout Mode</FormLabel>
                                                        <FormControl>
                                                            <ControlledCheckboxInput
                                                                checked={field.value ?? null}
                                                                onValue={knockout =>
                                                                    form.setValue(
                                                                        "standingsCalculation.knockout",
                                                                        knockout,
                                                                        {
                                                                            shouldDirty: true,
                                                                            shouldValidate: true,
                                                                        }
                                                                    )
                                                                }
                                                                className="w-full"
                                                            />
                                                        </FormControl>
                                                        <div className="mt-1 text-sm text-gray-500">
                                                            Only works for traditional knockout
                                                            bracket tournaments with elimination
                                                            rounds
                                                        </div>
                                                        <FormMessage />
                                                    </FormItem>
                                                )}
                                            />
                                        </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}

                    <Button
                        type="submit"
                        disabled={!form.formState.isDirty || form.formState.isSubmitting}
                    >
                        Save
                    </Button>
                </form>
            </Form>
        );
    }
);

TournamentForm.displayName = "TournamentForm";
